import React, {useState, useEffect, useRef} from 'react';
import cx from 'classnames';
import {useRouter} from 'next/router';
import {useTranslations, createTranslator} from 'next-intl';
import {Grid, Row, Col} from '@/grid';
import withMainLayout from 'layouts/main';
import {SectionPaddingMdToZero} from '@/section';
import Container from '@/container';
import Link from '@/links';
import {withBoldWeight} from '@/hocs/withFontType';
import {H2, H3, P, PMarginSm} from '@/typography';
import {Table, TBody, TH, TR, TD} from '@/ui/tables';
import {ListTerms, ListNumbered} from '@/ui/lists/list-icons';
import {
  withMarginBottomMd,
  withMarginBottomXl,
  withMarginBottomSm,
  withMarginTopDoubleLg,
  withMarginLeftXs,
  withMarginBottomXxl
} from '@/hocs/withSpacing';
import withBreakpoints from '@/hocs/withBreakpoints';
import {withVisibleDesktop993, withHiddenDesktop993} from '@/hocs/withResponsiveUtils';
import TermsForm from '@/ui/hubspotform/terms/terms-form';
import Breadcrumbs from '@/ui/breadcrumbs';
import Dropdown from '@/form-fields/dropdown';
import Tabs from '@/ui/tabs/tabs';
import LegalHero from '@/content/legal/hero';
import useAdditionalTermsTabItems from '@/ui/tabs/tab-items/additional-terms-tabs';
import {
  handlePrint,
  handleDropdown,
  scrollToTopOfPage,
  scrollableDropdownMob,
  setTabUrl
} from '@/ui/legal/legal.helper';
import {pageMessagesProps} from 'helpers/i18n';
import legal from '../legal.module.scss';
import shared from '../../../messages/en/shared.json';
import legalMessages from '../../../messages/en/legal.json';
import legalAdditionalTerms from '../../../messages/en/legalAdditionalTerms.json';

const seoMetas = props => {
  const seoT = createTranslator({locale: props.locale, messages: props.messages});
  return {
    title: seoT('legalAdditionalTerms.seo.title'),
    description: seoT('legalAdditionalTerms.seo.description'),
    canonical: seoT('legalAdditionalTerms.seo.canonical')
  };
};

const BreadcrumbsDesktop = withVisibleDesktop993(withMarginBottomXxl('div'));
const HiddenInDesktop = withHiddenDesktop993('div');
const BoldSpan = withBoldWeight('span');
const MarginedH2 = withMarginBottomMd(H2);
const H3BottomMarginedSm = withMarginBottomSm(H3);
const DivMarginedBottomXl = withMarginBottomXl('div');
const PriorVersionMargin = withMarginTopDoubleLg('div');
const DivMarginedLeftXl = withMarginLeftXs('div');
const List = props => <ListTerms {...props} />;
const ListOl = props => <ListNumbered {...props} />;

const ServiceLevelAgreement = () => {
  const t = useTranslations('legalAdditionalTerms.sla');

  return (
    <>
      <MarginedH2>{t('heading')}</MarginedH2>
      <DivMarginedBottomXl>
        <Link onClick={handlePrint} variant="secondary-no-gradient-download" className="no-print">
          {t('downloadLabel')}
        </Link>
      </DivMarginedBottomXl>

      <PMarginSm>{t('intro1')}</PMarginSm>

      <P>{t('intro2')}</P>

      <Table className={legal['legal-table']}>
        <TBody>
          <TR>
            <TH>{t('table.headers.incidentLevel')}</TH>
            <TH>{t('table.headers.criteria')}</TH>
            <TH>{t('table.headers.responseTime')}</TH>
            <TH>{t('table.headers.resolutionTime')}</TH>
          </TR>
          {t.raw('table.rows').map(row => (
            <TR key={row.level}>
              <TD>
                <PMarginSm>
                  <BoldSpan>{row.level}</BoldSpan>
                </PMarginSm>
              </TD>
              <TD>
                <PMarginSm>{row.criteria}</PMarginSm>
              </TD>
              <TD>
                <PMarginSm>{row.responseTime}</PMarginSm>
              </TD>
              <TD>
                <PMarginSm>{row.resolutionTime}</PMarginSm>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>

      <H3BottomMarginedSm className="heading-4">{t('definitions.title')}</H3BottomMarginedSm>
      <PMarginSm>{t('definitions.intro')}</PMarginSm>
      <List>
        <li>
          <BoldSpan>{t('definitions.downtimeLabel')}</BoldSpan> {t('definitions.downtimeText')}
        </li>
        <List>
          <li>
            <BoldSpan>{t('definitions.emergencyDowntimeLabel')}</BoldSpan>{' '}
            {t('definitions.emergencyDowntimeText')}
          </li>
          <li>
            <BoldSpan>{t('definitions.scheduledDowntimeLabel')}</BoldSpan>{' '}
            {t('definitions.scheduledDowntimeText')}
          </li>
          {t.raw('definitions.exclusionItems').map(item => (
            <li key={item}>{item}</li>
          ))}
        </List>
        <li>
          <BoldSpan>{t('definitions.monthlyUptimeLabel')}</BoldSpan> {t('definitions.monthlyUptimeText')}
          <br />
          <br />
          <img
            src="/static/images/terms/monthly-uptime-percentage.png"
            alt={t('definitions.monthlyUptimeImageAlt')}
          />
        </li>
      </List>

      <H3BottomMarginedSm className="heading-4">{t('uptimeSla.title')}</H3BottomMarginedSm>
      <PMarginSm>{t('uptimeSla.intro')}</PMarginSm>
      <List>
        {t.raw('uptimeSla.items').map(item => (
          <li key={item}>{item}</li>
        ))}
      </List>
      <P>{t('uptimeSla.footnote')}</P>

      <H3BottomMarginedSm className="heading-4">{t('updates.title')}</H3BottomMarginedSm>
      <PMarginSm>{t('updates.paragraph')}</PMarginSm>
      <PMarginSm>
        {t.rich('updates.statusPageRich', {
          a: chunks => (
            <Link variant="default" href="https://workable.statuspage.io/" target="_blank">
              {chunks}
            </Link>
          )
        })}
      </PMarginSm>
      <PriorVersionMargin className={legal['legal-content']}>
        <PMarginSm className="heading-6">
          <BoldSpan>{t('lastUpdated')}</BoldSpan>
        </PMarginSm>
      </PriorVersionMargin>
    </>
  );
};

const TextingPolicy = () => {
  const t = useTranslations('legalAdditionalTerms.texting');

  const renderSection = section => (
    <li key={section.title}>
      <H3BottomMarginedSm className="heading-4">{section.title}</H3BottomMarginedSm>
      {section.items ? (
        <>
          <P>{section.intro}</P>
          <List>
            {section.items.map(item => (
              <li key={item}>{item}</li>
            ))}
          </List>
          <P>{section.outro}</P>
        </>
      ) : (
        <P>{section.paragraph}</P>
      )}
    </li>
  );

  const sections = t.raw('sections');

  return (
    <>
      <MarginedH2>{t('heading')}</MarginedH2>
      <DivMarginedBottomXl>
        <Link onClick={handlePrint} variant="secondary-no-gradient-download" className="no-print">
          {t('downloadLabel')}
        </Link>
      </DivMarginedBottomXl>
      <P>
        {t.rich('introRich', {
          bold: chunks => <BoldSpan>{chunks}</BoldSpan>,
          a1: chunks => (
            <Link href=" https://www.workable.com/terms" variant="default">
              {chunks}
            </Link>
          ),
          a2: chunks => (
            <Link
              href="https://www.twilio.com/en-us/legal/messaging-policy"
              variant="default"
              target="_blank">
              {chunks}
            </Link>
          ),
          a3: chunks => (
            <Link href="https://www.twilio.com/en-us/legal/aup" variant="default" target="_blank">
              {chunks}
            </Link>
          ),
          a4: chunks => (
            <Link
              href="https://www.twilio.com/en-us/guidelines/us/sms"
              variant="default"
              target="_blank">
              {chunks}
            </Link>
          ),
          a5: chunks => (
            <Link href="https://www.twilio.com/en-us/legal/privacy" variant="default" target="_blank">
              {chunks}
            </Link>
          ),
          a6: chunks => (
            <Link
              href="https://help.twilio.com/articles/4410588996123-U-S-Carrier-Penalties-for-Non-Compliant-Messaging"
              variant="default"
              target="_blank">
              {chunks}
            </Link>
          ),
          a7: chunks => (
            <Link
              href="https://help.twilio.com/articles/360045004974-Forbidden-Message-Categories-in-the-US-and-Canada-Short-Code-Toll-Free-and-Long-Code"
              variant="default"
              target="_blank">
              {chunks}
            </Link>
          ),
          a8: chunks => (
            <Link
              href="https://www.twilio.com/en-us/legal/service-country-specific-terms"
              variant="default"
              target="_blank">
              {chunks}
            </Link>
          )
        })}
      </P>
      <ListOl>
        {sections.slice(0, 6).map(renderSection)}
        <li>
          <H3BottomMarginedSm className="heading-4">
            {t('reportingViolations.title')}
          </H3BottomMarginedSm>
          <P>
            {t.rich('reportingViolations.paragraphRich', {
              a: chunks => (
                <Link href="support@workable.com" target="_blank" variant="default">
                  {chunks}
                </Link>
              )
            })}
          </P>
        </li>
        {sections.slice(6).map(renderSection)}
      </ListOl>
      <DivMarginedLeftXl>
        <PriorVersionMargin>
          <PMarginSm className="heading-6">
            <BoldSpan>{t('lastUpdated')}</BoldSpan>
          </PMarginSm>
        </PriorVersionMargin>
      </DivMarginedLeftXl>
    </>
  );
};

const AdditionalTermsPage = () => {
  const router = useRouter();
  const dropdownRef = useRef();
  const [activeTab, setActiveTab] = useState('service-level-agreement');
  const [isDropdownFixed, setIsDropdownFixed] = useState(false);
  const t = useTranslations('legalAdditionalTerms');
  const legalT = useTranslations('legal');
  const tabItems = useAdditionalTermsTabItems();

  const ResponsiveMenu = withBreakpoints({
    desktop: () => (
      <Tabs
        activeTab={activeTab}
        title={t('sectionTitle')}
        tabItems={tabItems}
        setActiveTab={setActiveTab}
        router={router}
      />
    ),
    mobile: () => (
      <div
        ref={dropdownRef}
        className={cx(
          isDropdownFixed ? legal['legal-dropdown-fixed'] : legal['legal-dropdown'],
          'no-print'
        )}>
        <Dropdown
          name="additional-terms"
          defaultValue={activeTab}
          variant="green800"
          values={tabItems}
          onChange={item => handleDropdown(item, router, setActiveTab)}
        />
      </div>
    )
  });

  useEffect(() => {
    setTabUrl(activeTab, setActiveTab, router);
  }, []);

  useEffect(() => {
    scrollableDropdownMob(dropdownRef, setIsDropdownFixed);
  }, []);

  const activeTabContent = () => {
    if (activeTab === 'texting-policy') {
      return <TextingPolicy />;
    }
    return <ServiceLevelAgreement />;
  };

  return (
    <>
      <LegalHero title={t('sectionTitle')} />
      <SectionPaddingMdToZero>
        <Container>
          <Grid fluid>
            <BreadcrumbsDesktop>
              <Breadcrumbs
                clickableLink={{text: legalT('breadcrumb.legal'), urlCopy: '/legal'}}
                title={t('sectionTitle')}
                activeTab={tabItems.find(item => item.value === activeTab).text}
              />
            </BreadcrumbsDesktop>
            <Row>
              <Col xs={12} md={12} lg={3}>
                <ResponsiveMenu />
              </Col>
              <Col xs={12} md={12} lgOffset={1} lg={8} className={legal['scroll-mg-t']}>
                {activeTabContent()}
              </Col>
            </Row>
            <TermsForm />
            <HiddenInDesktop>
              <Link variant="go-to-top-arrow" onClick={scrollToTopOfPage} />
            </HiddenInDesktop>
          </Grid>
        </Container>
      </SectionPaddingMdToZero>
    </>
  );
};

export default withMainLayout(AdditionalTermsPage, seoMetas);

export const messagesForIntl = {shared, legal: legalMessages, legalAdditionalTerms};

export function getStaticProps() {
  return pageMessagesProps(messagesForIntl);
}
